import React, { useState, useEffect, useRef, useContext } from 'react';
import styles from './SkipTo.module.css';
import { CloseIcon } from '../../assets/closeIcon';
import { AppStateContext } from "../../state/AppProvider";

interface SkipToProps {
    isDarkMode: boolean;
    env: string
    isFocusTrapPaused: boolean;
    setIsFocusTrapPaused: (isPaused: boolean) => void;
}

const SkipTo: React.FC<SkipToProps> = ({ isDarkMode, env, isFocusTrapPaused, setIsFocusTrapPaused }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [lastFocusedElement, setLastFocusedElement] = useState<HTMLElement | null>(null);
    const [tabPressedOnce, setTabPressedOnce] = useState(false);
    const [isQuestionInputFocused, setIsQuestionInputFocused] = useState(true);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const menuItemsRef = useRef<HTMLAnchorElement[]>([]);
    const justOpenedRef = useRef(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const [announcement, setAnnouncement] = useState("");
    const isFocusDetectionPausedRef = useRef(false);

    const appStateContext = useContext(AppStateContext)
    var branding = appStateContext?.state.frontendSettings?.branding;
    const useHighlight = branding?.show_skip_nav_highlight === "Y" ? true : false;

    const shortcuts = ['first-chat-message','faq-button','question-input-textarea','user-terms-button']

    // Allows first tab when switching programs
    useEffect(() => {
        // Focus on the input text area
        const questionInput = document.getElementById('question-input-textarea') as HTMLTextAreaElement;
        if (questionInput) {
            questionInput.focus();
            setIsQuestionInputFocused(true);
        }
    
        // Reset tabPressedOnce to false
        setTabPressedOnce(false);
    }, [env]);

    // Catches inputs for mouse and keys to track shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const questionInput = document.getElementById('question-input-textarea');
            // Opens skipnav menu if tab is pressed while focus is on the question input
            if (event.key === 'Tab' && document.activeElement === questionInput && isQuestionInputFocused) {
                if (!tabPressedOnce) {
                    event.preventDefault();
                    setIsVisible(true);
                    setTabPressedOnce(true);
                    justOpenedRef.current = true;
                } else {
                    setIsVisible(false);
                }
            }
            // Shortcut for FAQ button
            if (event.altKey && event.key.toLowerCase() === 'q') {
                const faqButton = document.getElementById('faq-button');
                handleHotkeyFocus("faq-button");
                if (faqButton) {
                    setTimeout(() => {
                        faqButton.focus();
                    }, 100);
                useHighlight ? (isDarkMode ? faqButton.classList.add(styles.focusedDM) : faqButton.classList.add(styles.focusedDM)) : null;
                if (lastFocusedElement) {
                    lastFocusedElement.classList.remove(styles.focused);
                    lastFocusedElement.classList.remove(styles.focusedDM);
                }
                setLastFocusedElement(faqButton);
                }
            }
            // Shortcut to open skipnav menu
            if (event.shiftKey && event.altKey && event.key.toLowerCase() === 'z') {
                handleHotkeyFocus("menuitem-first-chat-message");
                setIsVisible((prev) => !prev);
                setIsQuestionInputFocused(false);
            }
            // Shortcut for Question Input Box
            if (event.altKey && event.key.toLowerCase() === 'b') {
                const questionInput = document.getElementById('question-input-textarea');
                handleHotkeyFocus("question-input-textarea");
                if (questionInput) {
                    setTimeout(() => {
                        questionInput.focus();
                    }, 100);
                useHighlight ? (isDarkMode ? questionInput.classList.add(styles.focusedDM) : questionInput.classList.add(styles.focused)) : null;
                if (lastFocusedElement) {
                    lastFocusedElement.classList.remove(styles.focused);
                    lastFocusedElement.classList.remove(styles.focusedDM);
                }
                setLastFocusedElement(questionInput);
                }
            }
            // Shortcut to Terms button
            if (event.shiftKey && event.altKey && event.key.toLowerCase() === 'f') {
                const termsButton = document.getElementById('user-terms-button');
                handleHotkeyFocus("user-terms-button");
                if (termsButton) {
                    setTimeout(() => {
                        termsButton.focus();
                    }, 100);
                useHighlight ? (isDarkMode ? termsButton.classList.add(styles.focusedDM) : termsButton.classList.add(styles.focused)) : null;
                if (lastFocusedElement) {
                    lastFocusedElement.classList.remove(styles.focused);
                    lastFocusedElement.classList.remove(styles.focusedDM);
                }
                setLastFocusedElement(termsButton);
                }
            }
        };

        // Handles Enter key for FAQ button
        const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            const faqButton = document.getElementById('faq-button');
            if (faqButton && document.activeElement === faqButton) {
            faqButton.click();
            }
        }
        };

        // Tracks focus for first tab
        const handleFocusOut = (event: FocusEvent) => {
            if (justOpenedRef.current) {
                justOpenedRef.current = false;
                return;
            }
            const questionInput = document.getElementById('question-input-textarea');
            if (event.target === questionInput) {
                if (isQuestionInputFocused) {
                    setIsVisible(false);
                }
                setIsQuestionInputFocused(false);
            }
        };
        
        // Tracks current focused item
        const handleFocusIn = (event: FocusEvent) => {
            if (lastFocusedElement && event.target !== lastFocusedElement) {
                lastFocusedElement.classList.remove(styles.focused);
                lastFocusedElement.classList.remove(styles.focusedDM);
                setLastFocusedElement(null);
            }
        };
        
        // Tracks if mouse is clicked outside of the menu
        const handleMouseDown = (event: MouseEvent) => {
            if (lastFocusedElement && !lastFocusedElement.contains(event.target as Node)) {
                lastFocusedElement.classList.remove(styles.focused);
                lastFocusedElement.classList.remove(styles.focusedDM);
                setLastFocusedElement(null);
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keypress', handleKeyPress);
        window.addEventListener('focusout', handleFocusOut);
        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('mousedown', handleMouseDown);
        
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keypress', handleKeyPress);
            window.removeEventListener('focusout', handleFocusOut);
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('mousedown', handleMouseDown);
        };
    }, [lastFocusedElement, tabPressedOnce, isQuestionInputFocused]);

    // Focus on the first menu item when the menu becomes visible
    useEffect(() => {
        if (isVisible) {
            isFocusDetectionPausedRef.current = true;
            setTimeout(() => {
                menuItemsRef.current[0]?.focus();
                setFocusedIndex(0);
                justOpenedRef.current = false;
            }, 200);

            setTimeout(() => {
                isFocusDetectionPausedRef.current = false; // Resume focus detection after 500ms
            }, 500);
        }
    }, [isVisible]);

    // Controls skipnav menu navigation
    const handleNavigation = (event: React.KeyboardEvent<HTMLDivElement>) => {
        //console.log('event.key:', event.key);
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setFocusedIndex((prevIndex) => (prevIndex + 1) % menuItemsRef.current.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setFocusedIndex((prevIndex) => (prevIndex - 1 + menuItemsRef.current.length) % menuItemsRef.current.length);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            handleHotkeyFocus('question-input-textarea')
            menuItemsRef.current[focusedIndex]?.click();
            handleClose();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            handleClose();
        } else if (event.key === 'Tab') {
            event.preventDefault();
            handleClose();
            const speechButton = document.getElementById('speechbutton');
            if (speechButton) {
                speechButton.focus();
            }
        }
    };

    // Focus on the currently selected menu item
    useEffect(() => {
        menuItemsRef.current[focusedIndex]?.focus();
    }, [focusedIndex]);

    // Handles shortcut for main content
    const handleSkipToMainContent = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault(); // Prevent default anchor behavior
        const mainContent = document.getElementById('first-chat-message');
        if (mainContent) {
            mainContent.focus();
            useHighlight ? (isDarkMode ? mainContent.classList.add(styles.focusedDM) : mainContent.classList.add(styles.focused)) : null;
            if (lastFocusedElement) {
                lastFocusedElement.classList.remove(styles.focused);
                lastFocusedElement.classList.remove(styles.focusedDM);
            }
            setLastFocusedElement(mainContent);
        }
        handleClose();
    };

    const handleHotkeyFocus = (elementId: string) => {
        const targetElement = document.getElementById(elementId);
        if (targetElement) {
            setIsFocusTrapPaused(true); // Pause the focus trap
    
            // Add a focus event listener to detect when the focus has shifted
            const handleFocusChange = (event: FocusEvent) => {
                if (
                    event.target !== targetElement &&
                    !document.getElementById('skiptomainmenu')?.contains(event.target as Node) &&
                    !shortcuts.some(id => document.getElementById(id) === event.target)
                ) {
                    // Focus has shifted to the target element, reactivate the focus trap
                    setIsFocusTrapPaused(false);
                    window.removeEventListener("focusin", handleFocusChange);
                
                }
            };
            setTimeout(() => {
                window.addEventListener("focusin", handleFocusChange);
            }, 400);
        }
    };
    
    // Handles shortcut for FAQ button
    const handleSkipToFAQ = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault(); // Prevent default anchor behavior
        const faqButton = document.getElementById('faq-button');
        handleHotkeyFocus("faq-button");
        if (faqButton) {
            faqButton.focus();
            useHighlight ? (isDarkMode ? faqButton.classList.add(styles.focusedDM) : faqButton.classList.add(styles.focused)) : null;
            if (lastFocusedElement) {
            lastFocusedElement.classList.remove(styles.focused);
            lastFocusedElement.classList.remove(styles.focusedDM);
            }
            setLastFocusedElement(faqButton);
        }
        handleClose();
    };

    // Handles shortcut for Question Input Box
    const handleSkipToQuestionBox = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault(); // Prevent default anchor behavior
        const questionInput = document.getElementById('question-input-textarea');
        handleHotkeyFocus("question-input-textarea");
        if (questionInput) {
        questionInput.focus();
        useHighlight ? (isDarkMode ? questionInput.classList.add(styles.focusedDM) : questionInput.classList.add(styles.focused)) : null;
        if (lastFocusedElement) {
            lastFocusedElement.classList.remove(styles.focused);
            lastFocusedElement.classList.remove(styles.focusedDM);
        }
        setLastFocusedElement(questionInput);
        }
        handleClose();
    };

    // Handles shortcut for skipnav menu
    const handleToggleShortcuts = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault(); 
        setIsVisible((prev) => !prev);
    };

    // Handles shortcut for footer
    const handleSkipToFooter = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault(); 
        const termsButton = document.getElementById('user-terms-button');
        handleHotkeyFocus("user-terms-button");
        if (termsButton) {
        termsButton.focus();
        useHighlight ? (isDarkMode ? termsButton.classList.add(styles.focusedDM) : termsButton.classList.add(styles.focused)) : null;
        if (lastFocusedElement) {
            lastFocusedElement.classList.remove(styles.focused);
            lastFocusedElement.classList.remove(styles.focusedDM);
        }
        setLastFocusedElement(termsButton);
        }
        handleClose();
    };

    // Handles closing the skipnav menu
    const handleClose = () => {
        setIsVisible(false);
        justOpenedRef.current = false;
    };

    // Handles focus highlighting for items selected through shortcuts
    useEffect(() => {
        const handleFocusChange = (event: FocusEvent) => {
            const focusedElement = document.activeElement;
    
            // Remove the focusedItem class from all menu items
            menuItemsRef.current.forEach((item) => {
                if (item) {
                    item.classList.remove(styles.focused);
                    item.classList.remove(styles.focusedDM);
                }
            });
    
            // Add the focusedItem class to the currently focused element
            if (focusedElement && menuItemsRef.current.includes(focusedElement as HTMLAnchorElement)) {
                if (isDarkMode) {
                    focusedElement.classList.add(styles.focusedDM);
                } else {
                    focusedElement.classList.add(styles.focused);
                }
            }
    
            // Close the box if the focus is outside the box or its elements
            if (!isFocusDetectionPausedRef.current && focusedElement && !boxRef.current?.contains(focusedElement)) {
                handleClose();
            }
        };
    
        window.addEventListener('focusin', handleFocusChange);
    
        return () => {
            window.removeEventListener('focusin', handleFocusChange);
        };
    }, [isDarkMode]);
    
    useEffect(() => {
        // Update styles for focused elements when isDarkMode changes
        menuItemsRef.current.forEach((item) => {
            if (item) {
                item.classList.remove(styles.focused);
                item.classList.remove(styles.focusedDM);
                if (document.activeElement === item) {
                    if (isDarkMode) {
                        item.classList.add(styles.focusedDM);
                    } else {
                        item.classList.add(styles.focused);
                    }
                }
            }
        });
    }, [isDarkMode]);

    return (
        <div id='skiptomainmenu' role="menu" aria-describedby="skipToDescription" ref={boxRef} className={`${styles.skipToBox} ${isVisible ? styles.visible : ''} ${isDarkMode ? styles.darkMode : styles.lightMode}`} onKeyDown={handleNavigation} data-theme={isDarkMode ? "dark" : ""}>
            <div id="skipToDescription" className={styles.visuallyHidden}>
              Keyboard shortcuts opened. Press arrow keys to navigate.
            </div>
            <div
                onClick={handleClose}
                role="button"
                tabIndex={0}
                aria-label="Close"
                title="Close"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleClose();
                    }
                }}
                style={{ position: 'absolute', top: '5px', right: '5px', cursor: 'pointer' }}
            >
                <CloseIcon fill="var(--YW_Panels_Icons_Color)"/>
            </div>
            <div className={`${styles.skipToTitleText} ${isDarkMode ? styles.darkMode : styles.lightMode} ${styles.titleText}`}>Skip to</div>
            <a id='menuitem-first-chat-message' role="menuitem" href="#first-chat-message" onClick={handleSkipToMainContent} className={`${styles.skipToLink} ${isDarkMode ? styles.darkMode : styles.lightMode}`} ref={(el) => (menuItemsRef.current[0] = el!)}>
                <span className={styles.mainContentLink}>Main Content</span>
            </a>
            <hr className={styles.separator} />
            <div className={`${styles.skipToTitleText} ${isDarkMode ? styles.darkMode : styles.lightMode} ${styles.titleText}`}>Keyboard Shortcuts</div>
            <a role="menuitem" href="#faq-button" onClick={handleSkipToFAQ} className={`${styles.skipToLink} ${isDarkMode ? styles.darkMode : styles.lightMode}`} ref={(el) => (menuItemsRef.current[1] = el!)}>
                <span className={styles.shortcutTitle}>Frequently Asked Questions</span>
                <span className={styles.shortcutKeys}>
                    <span className={styles.key}>Alt</span>
                    <span className={styles.plusSign}>+</span>
                    <span className={styles.key}>Q</span>
                </span>
            </a>
            <a role="menuitem" href="#question-input-textarea" onClick={handleSkipToQuestionBox} className={`${styles.skipToLink} ${isDarkMode ? styles.darkMode : styles.lightMode}`} ref={(el) => (menuItemsRef.current[2] = el!)}>
                <span className={styles.shortcutTitle}>Question Box</span>
                <span className={styles.shortcutKeys}>
                    <span className={styles.key}>Alt</span>
                    <span className={styles.plusSign}>+</span>
                    <span className={styles.key}>B</span>
                </span>
            </a>
            <a role="menuitem" href="#user-terms-button" onClick={handleSkipToFooter} className={`${styles.skipToLink} ${isDarkMode ? styles.darkMode : styles.lightMode}`} ref={(el) => (menuItemsRef.current[3] = el!)}>
                <span className={styles.shortcutTitle}>Footer</span>
                <span className={styles.shortcutKeys}>
                    <span className={styles.key}>Shift</span>
                    <span className={styles.plusSign}>+</span>
                    <span className={styles.key}>Alt</span>
                    <span className={styles.plusSign}>+</span>
                    <span className={styles.key}>F</span>
                </span>
            </a>
            <a role="menuitem" href="#" onClick={handleToggleShortcuts} className={`${styles.skipToLink} ${isDarkMode ? styles.darkMode : styles.lightMode}`} ref={(el) => (menuItemsRef.current[4] = el!)}>
                <span className={styles.shortcutTitle}>Show/Hide shortcuts</span>
                <span className={styles.shortcutKeys}>
                    <span className={styles.key}>Shift</span>
                    <span className={styles.plusSign}>+</span>
                    <span className={styles.key}>Alt</span>
                    <span className={styles.plusSign}>+</span>
                    <span className={styles.key}>Z</span>
                </span>
            </a>
        </div>
    );
};

export default SkipTo;